import { ChassisLevel } from '@components/arsenal/chassis/ChassisLevel';
import { capitalize } from '@helpers/dotNotation';
import { useTranslation } from 'react-i18next';
import { useUserInfoQuery } from '@http';
import * as Shared from '@shared/lib';

interface PlayerInfoProps {
  playerId: string;
};

export function PlayerInfo({ playerId }: PlayerInfoProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'PlayerInfo' });
  const { t: tAllies } = useTranslation(undefined, { keyPrefix: 'Hub.Allies' });
  const { data: user } = useUserInfoQuery(playerId);

  if (!user) return (
    <div className="flex justify-center my-6">
      <span className="loader" />
    </div>
  );

  const weaponXp = user.weapons.reduce((a, b) => {
    const prestigeBase = b.prestige * Shared.WeaponLevelCalculator.getXpForLevel(30);
    return a + prestigeBase + b.xp;
  }, 0);
  const chassisXp = user.chassis.reduce((a, b) => {
    const prestigeBase = b.prestige * Shared.ChassisLevelCalculator.getXpForLevel(30);
    return a + prestigeBase + b.xp;
  }, 0);
  const xp = weaponXp + chassisXp;

  const level = Shared.PlayerLevelCalculator.calc(xp);
  const progress = Math.min(100, (xp - level.base) / level.toNext * 100);

  return (
    <>
      {/* Player */}
      <div className="flex p-2 bg-tertiary">
        <img src={`img/avatars/${user.avatar}.avif`} className="w-10 h-10" />
        <div className="flex flex-col justify-center ml-2 w-full">
          <p>{user.username}</p>
          <div className="w-full h-4 relative bg-secondary flex items-center p-0.5">
            <div className="bg-primary/40 h-full absolute inset-0" style={{ width: `${progress}%` }} />
            <p className="z-10 text-xs text-center w-full">
              {t('level')} {level.current}
            </p>
          </div>
        </div>
      </div>
      {/* Player END */}

      <div className="p-2">
        {/* Chassis */}
        <div className="mt-2">
          <div className="flex items-center justify-between">
            <p className="text-lg">{t('chassis')}</p>
            <p className="active text-sm">{user.chassis.length} / {Shared.chassisData.length}</p>
          </div>

          {user.chassis.map(chassis => (
            <div className="border-t border-x border-secondary flex">
              <div className="w-16 h-16 flex-shrink-0 relative border-r border-secondary pt-1">
                <div className="absolute inset-0 grid-overlay" />
                <img
                  className="w-full h-full object-contain relative"
                  src={`/img/chassis/${chassis.key}.avif`}
                />
              </div>
              <div className="px-2 flex flex-col justify-center w-full">
                <p className="mb-1">{capitalize(chassis.key)}</p>
                <ChassisLevel xp={chassis.xp} />
              </div>
            </div>
          ))}

          <div className="border-t border-secondary w-full" />
        </div>
        {/* Chassis END */}

        {/* Weapons */}
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <p className="text-lg">{t('weapons')}</p>
            <p className="active text-sm">{user.weapons.length} / {Shared.weaponsData.length}</p>
          </div>

          {user.weapons.map(weapon => (
            <div className="border-t border-x border-secondary flex">
              <div className="w-16 h-16 flex-shrink-0 relative border-r border-secondary px-1">
                <div className="absolute inset-0 grid-overlay" />
                <img
                  className="w-full h-full object-contain relative -rotate-25"
                  src={`/img/weapons/${weapon.key}.avif`}
                />
              </div>
              <div className="px-2 flex flex-col justify-center w-full">
                <p className="mb-1">{capitalize(weapon.key)}</p>
                <ChassisLevel xp={weapon.xp} />
              </div>
            </div>
          ))}

          <div className="border-t border-secondary w-full" />
        </div>
        {/* Weapons END */}

        {/* Allies */}
        <div className="mt-4">
          <p className="text-lg">{t('allies')}</p>

          {user.allies.map(ally => (
            <div className="border-t border-x border-secondary flex">
              <div className="w-16 h-16 flex-shrink-0 relative border-r border-secondary">
                <div className="absolute inset-0 grid-overlay" />
                <img
                  src={`/img/allies/${ally.type.toLowerCase()}.avif`}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="px-2 flex flex-col justify-center w-full">
                <p className="mb-2">{tAllies(`${ally.type}.name`)}</p>
                <div className="flex items-center pl-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div className={`${ally.rank > i && 'active'} ally-rank`} key={i} />
                  ))}
                </div>
              </div>
            </div>
          ))}
          <div className="border-t border-x border-secondary w-full" />
        </div>
        {/* Allies END */}
      </div>
    </>
  );
}